import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import * as Actions from 'actions'
import Button from 'components/base/Button'
import Input from 'components/base/Input'
import MenuItem from 'components/base/MenuItem'
import Scrollbars from 'components/base/Scrollbars'
import Select from 'components/base/Select'
import { TranslationContext } from 'contexts/TranslationContext'
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { AppRoutes } from '~/AppRoutes'
import { ReactComponent as CloseIcon } from '~/assets/settings/navigation_close.svg'
import {
  NW_JS_APP_PROFILES,
  FILE_STATUS,
  MEDIA_TYPE,
  SOURCE_FILE_TYPES,
  TASK_STATUS,
  HIEROGLYPH_LANG
} from '~/enums'
import { useAction } from '~/hooks'
import * as Selectors from '~/selectors'
import ErrorText from './ErrorText'
import './SaveSettings.scss'
import Status from './Status'
import { getLang } from '~/helpers/getLang'
import { setSaveSettingsMode } from '~/actions/mainView'
import { SAVE_SETTINGS_MODE } from '~/config/constants/mainView'
import { selectRenderSettings } from '~/selectors/mainView'
import { DEFAULT_PROJECT_OUTPUT_NAME } from '~/constant'
import { pathSep } from '~/Util'

const STEP = {
  INITIAL: 'initial',
  STATUS: 'status',
}

// TODO: refactor this mix of editing task info and sourceFile info (http://18.184.210.86/issues/1235#note-6)
const SaveSettings = forwardRef((_, ref) => {
  const { t } = useContext(TranslationContext)
  const [ confirm, setConfirm ] = React.useState(false)
  const history = useHistory()
  const abortControllerRef = useRef(new AbortController())
  const onCloseSettings = useAction(Actions.mainView.showSaveSettings, false)
  const setDefaultMode = useAction(setSaveSettingsMode, SAVE_SETTINGS_MODE.DEFAULT)
  const tasks = useSelector(state => state.project.editingTasks)
  const error = useSelector(state => state.project.error)
  const selectedTaskId = useSelector(state => state.project.selectedTaskId)
  const selectedClipId = useSelector(state => state.playback.selectedClipId)
  const selectedClip = useSelector(state => Selectors.getSourceFileById(state, selectedClipId))
  const isTaskLoading = useSelector(state => state.project.loading)
  const isViewLoading = useSelector(Selectors.selectIsViewLoading)
  const prefferedFormat = useSelector(Selectors.getPrefferedOutputFormat)
  const saveSettingsMode = useSelector(state => state.mainView.saveSettingsMode)
  const { askPublishOutputName, closePublishSettingsOnCancel } = useSelector(selectRenderSettings)
  const absFilePath = useSelector(state => state.project.absFilePath)
  const outputName = useSelector(state => state.project.outputName === DEFAULT_PROJECT_OUTPUT_NAME
    ? t('PUBLISH_SETTINGS_OUTPUT_NAME')
    : state.project.outputName)
  const outputPath = useSelector(state => state.project.outputPath)
  const isLoading = isViewLoading
  const isNewPublish = saveSettingsMode === SAVE_SETTINGS_MODE.NEW_PUBLISH

  const task = selectedTaskId && !isNewPublish
    ? tasks.find(t => t.id === selectedTaskId) : null
  const sourceFile = __CFG__.SAVE_SETTINGS.CREATE_OUTPUT_MEDIA && !isNewPublish
    ? selectedClip : null

  const getCaption = () => {
    if (task?.status === TASK_STATUS.COMPLETED || sourceFile?.status === FILE_STATUS.READY) {
      return t('SAVE_SETTINGS_CAPTION_VIDEO_RENDER_COMPLETE')
    }
    return t('SAVE_SETTINGS_CAPTION_PUBLISH_SETTINGS')
  }


  const [ step, setStep ] = useState(task || isTaskLoading ? STEP.STATUS : STEP.INITIAL)
  const [ format, setFormat ] = useState(prefferedFormat)
  const onChangeFormat = useCallback(e => {
    setFormat(e.target.value)
  }, [ setFormat ])

  const [ name, setName ] = useState(outputName)
  const onChangeName = useCallback(e => {
    const value = e.target.value.slice(0, __CFG__.SAVE_PROJECT_MENU.MAX_FILENAME_LENGTH)
    setName(value)
  }, [])

  const formattedNamed = `${name}.${format.toLowerCase()}`
  const createTaskOutputName = NW_JS_APP_PROFILES.includes(__APP_PROFILE__)
    ? `${outputPath}${pathSep()}${outputName}`
    : name

  const createTask = useAction(Actions.project.requestCreateTask, createTaskOutputName, format)
  const setCurAbsPathName = useAction(Actions.project.setAbsFilePath)
  const deleteTask = useAction(Actions.project.deleteTask, task?.id)
  const resetTaskError = useAction(Actions.project.resetEditingTaskError)
  const deleteOutputFile = useAction(
    Actions.sourceFiles.requestDeleteFile, task?.fileId, SOURCE_FILE_TYPES.MEDIA
  )

  const requestDeleteOutputFile = useCallback(() => {
    if (!NW_JS_APP_PROFILES.includes(__APP_PROFILE__)) {
      deleteOutputFile()
    }
  }, [ deleteOutputFile ])


  const onClose = useCallback(() => {
    onCloseSettings()
    setDefaultMode()
    resetTaskError()
  }, [ setDefaultMode, onCloseSettings, resetTaskError ])

  const onSubmit = useCallback(() => {
    abortControllerRef.current = new AbortController()
    setStep(STEP.STATUS)
    setDefaultMode()
    createTask(abortControllerRef.current)
  }, [ createTask, setDefaultMode ])


  const onCancel = useCallback(() => {
    if (task?.status === TASK_STATUS.PREPARE_IMAGES) {
      abortControllerRef.current.abort()
    } else {
      requestDeleteOutputFile()
      deleteTask()
    }
    // reset prepare images error in the next tick
    setTimeout(() => resetTaskError())
    if (closePublishSettingsOnCancel) {
      onClose()
    } else {
      setStep(STEP.INITIAL)
    }
  }, [
    requestDeleteOutputFile, task, deleteTask, onClose, abortControllerRef,
    resetTaskError, closePublishSettingsOnCancel,
  ])

  const onCloseClick = useCallback(() => {
    if (__CFG__.SAVE_SETTINGS.CLOSE_CONFIRMATION_ENABLED && task) {
      setConfirm(true)
    } else if (__CFG__.SAVE_SETTINGS.DELETE_OUTPUT_ON_CLOSE && task) {
      requestDeleteOutputFile()
      deleteTask()
      onClose()
    } else {
      onClose()
    }
  }, [ task, onClose, requestDeleteOutputFile, deleteTask ])

  const onConfirmationCanceled = useCallback(() => {
    setConfirm(false)
  }, [])

  const clearTimeline = useAction(Actions.timeline.clearTimeline)
  const onConfirm = useCallback(() => {
    const { status } = task
    if (status !== TASK_STATUS.PREPARE_IMAGES) {
      requestDeleteOutputFile()
      deleteTask()
    }
    if (__CFG__.SAVE_SETTINGS.REDIRECT_TO_PROJECTS_ON_CLOSE
      && status !== TASK_STATUS.ABORTED) {
      history.push(AppRoutes.Projects)
    }
    if (__CFG__.SAVE_SETTINGS.CLEAR_TIMELINE_ON_CLOSE) {
      clearTimeline()
    }
    onClose()
  }, [ task, onClose, deleteTask, requestDeleteOutputFile, clearTimeline, history ])

  const refScrollAPI = useRef()
  useEffect(() => {
    refScrollAPI.current.scrollToBottom()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ task ])

  useEffect(() => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Will only work for desktop versions
  useEffect(() => {
    if (absFilePath && !task) {
      onSubmit()
      setCurAbsPathName('')
    }
  }, [ absFilePath, task, onSubmit, setCurAbsPathName ])

  useEffect(() => {
    if (!askPublishOutputName
      && !NW_JS_APP_PROFILES.includes(__APP_PROFILE__)
      && isNewPublish && step === STEP.INITIAL && !task && !isTaskLoading) {
      onSubmit()
    }
  }, [ askPublishOutputName, isNewPublish, step, task, isTaskLoading, onSubmit ])

  useImperativeHandle(ref, () => ({ onSubmit, onClose }))

  return (
    <div className="save-settings">
      <div className="save-settings__head allow-draggable">
        <div className="save-settings__top">
          <span className="save-settings__caption">
            {getCaption()}
          </span>
          <div className="save-settings__close" onClick={onCloseClick}>
            <CloseIcon />
          </div>
          {/* TODO: remove after demo */}
          {confirm && <Confirmation onCancel={onConfirmationCanceled} onClose={onConfirm} />}
        </div>

        <If condition={!isLoading && !error}>
          <Choose>
            <When condition={step === STEP.INITIAL
              && (!sourceFile || sourceFile?.status !== FILE_STATUS.READY)}
            >
              <Controls
                name={name}
                onChangeName={onChangeName}
                format={format}
                onChangeFormat={onChangeFormat}
                onSubmit={onSubmit}
                askPublishOutputName={askPublishOutputName}
              />
            </When>

            <Otherwise>
              <ControlsStatus
                outputName={sourceFile?.name || name}
                onCancel={onCancel}
                status={task?.status}
                askPublishOutputName={askPublishOutputName}
              />
            </Otherwise>
          </Choose>
        </If>
      </div>
      <Scrollbars trackHorizontalSize={1} ref={refScrollAPI} style={{ height: 'calc(100% - 71px)' }}>
        <div className="save-settings__body">
          <If condition={__CFG__.SAVE_PROJECT_MENU.DETAILED_PROJECT_STATS_ON_SAVE}>
            <Info />
          </If>
          <If condition={step === STEP.STATUS || sourceFile?.status === FILE_STATUS.READY}>
            <div className="save-settings__bottom">
              {!error && (task || sourceFile?.status === FILE_STATUS.READY)
                && (
                <Status
                  sourceFile={sourceFile}
                  task={task}
                  outputName={formattedNamed}
                />
                )}
              <ErrorText text={error?.message} isError />
            </div>
          </If>
        </div>
      </Scrollbars>
    </div>
  )
})
function Controls({ name, onChangeName, format, onChangeFormat, onSubmit, askPublishOutputName }) {
  const { t } = useContext(TranslationContext)
  return (
    <div className="save-settings__control">
      <If condition={askPublishOutputName !== 0}>
        <If condition={__CFG__.SAVE_PROJECT_MENU.FILENAME_TITLE}>
          <span className="save-settings__title">
            {t('SAVE_SETTINGS_TEXT_TITLE')}
            :
          </span>
        </If>
        <div className="save-settings__name">
          <Input
            value={name}
            onChange={onChangeName}
            placeholder={t('SAVE_SETTINGS_INPUT_PLACEHOLDER_RENDER_NAME')}
          />
        </div>
        <div className="save-settings__format">
          <If condition={__CFG__.SAVE_PROJECT_MENU.CHOOSE_OUTPUT_FILE_FORMAT}>
            <Select
              value={format}
              onChange={onChangeFormat}
            >
              <MenuItem value={MEDIA_TYPE.MP4}>{MEDIA_TYPE.MP4}</MenuItem>
              <MenuItem value={MEDIA_TYPE.MXF}>{MEDIA_TYPE.MXF}</MenuItem>
              <MenuItem value={MEDIA_TYPE.TS}>{MEDIA_TYPE.TS}</MenuItem>
            </Select>
          </If>
        </div>
      </If>
      <div className="save-settings__btn-start" title={t('SAVE_SETTINGS_BTN_RENDER_START_TOOLTIP')}>
        <Button
          primary
          disabled={name?.length === 0}
          onClick={onSubmit}
          data-lang-id="SAVE_SETTINGS_BTN_RENDER_START"
        >
          {t('SAVE_SETTINGS_BTN_RENDER_START')}
        </Button>
      </div>
    </div>
  )
}

function ControlsStatus({ onCancel, outputName, status, askPublishOutputName }) {
  const showCancel = (status && status !== TASK_STATUS.COMPLETED)
  const { t } = useContext(TranslationContext)
  return (
    // TODO: Add 'Save Complete!' when status is completed
    <div className="save-settings__descr">
      <If condition={askPublishOutputName !== 0}>
        <span className="save-settings__fullname">{`${t('PUBLISH_SETTINGS_TITLE')}: ${outputName}`}</span>
      </If>
      <div className="save-settings__btn-cancel">
        {showCancel && <Button className={HIEROGLYPH_LANG.includes(getLang()) ? 'fs15' : ''} onClick={onCancel} secondary>{t('SAVE_SETTINGS_BTN_CANCEL')}</Button>}
      </div>
    </div>
  )
}

function Info() {
  const { width, height } = useSelector(Selectors.getReferenceVideoAsset)
  const { t } = useContext(TranslationContext)
  return (
    <ul className="save-settings__info">
      <li className="save-settings__info-item">
        {t('SAVE_SETTINGS_LIST_ITEM_SIZE_APPROXIMATE')}
        :
        <span> 101,7 MB</span>
      </li>
      <li className="save-settings__info-item">
        {t('SAVE_SETTINGS_LIST_ITEM_RESOLUTION')}
        :
        <span>
          {' '}
          {width}
          {' '}
          x
          {' '}
          {height}
        </span>
      </li>
      <li className="save-settings__info-item">
        {t('SAVE_SETTINGS_LIST_ITEM_DURATION')}
        :
        <span> 5 m 35 s </span>
      </li>
      <li className="save-settings__info-item">
        {t('SAVE_SETTINGS_LIST_ITEM_CODEC')}
        :
        <span> AAC, H.264</span>
      </li>
      <li className="save-settings__info-item">
        {t('SAVE_SETTINGS_LIST_ITEM_FRAME_RATE')}
        {' '}
        (fps):
        <span> 25 fps</span>
      </li>
      <li className="save-settings__info-item">
        {t('SAVE_SETTINGS_LIST_ITEM_AUDIO')}
        :
        <span>
          {' '}
          44100
          {' '}
          {t('SAVE_SETTINGS_LIST_ITEM_HZ_STEREO')}
        </span>
      </li>
    </ul>
  )
}

function Confirmation({ onCancel, onClose }) {
  const { t } = useContext(TranslationContext)
  return (
    <Dialog disableBackdropClick open onClose={onCancel}>
      <DialogTitle>{__CFG__.SAVE_SETTINGS.CONFIRMATION_TITLE}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('SAVE_SETTINGS_LINK_TO_TRANSCODING_WILL_BE_LOST')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button primary onClick={onCancel} title={t('SAVE_SETTINGS_BTN_CANCEL_TOOLTIP')} data-lang-id="SAVE_SETTINGS_BTN_CANCEL">
          {t('SAVE_SETTINGS_BTN_CANCEL')}
        </Button>
        <Button primary onClick={onClose} autoFocus title={t('SAVE_SETTINGS_BTN_OK_TOOLTIP')} data-lang-id="SAVE_SETTINGS_BTN_OK">
          {t('SAVE_SETTINGS_BTN_OK')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaveSettings
